<script setup lang="ts">
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== 'dark');

interface WhyItem {
  title: string;
  description: string;
}

interface OverlayPosition {
  top: string;
  left: string;
}

const props = withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    bgImageDark?: string;
    bgImageLight?: string;
    items?: WhyItem[];
    overlayPositions?: OverlayPosition[];
  }>(),
  {
    heading: 'Why We Are Different',
    subcopy:
      'Unique, strategic, and fast creatives crafted just for your brand\'s success.',
    bgImageDark: '/images/why-are-we-different-dark.svg',
    bgImageLight: '/images/why-are-we-different-light.svg',
    items: () => [
      {
        title: 'Creativity Meets Strategy',
        description:
          'We blend creative design with strategy to deliver content that looks great and drives results.',
      },
      {
        title: 'Trend-Driven Design',
        description:
          'Our work is guided by the latest social media trends and backed by analytics for maximum impact.',
      },
      {
        title: 'Custom-Crafted Visuals',
        description:
          'Each visual is crafted uniquely for your brand never using templates so your content always stands out.',
      },
      {
        title: 'Fast & Flexible',
        description:
          'Quick delivery paired with unlimited revisions ensures your satisfaction every step of the way.',
      },
      {
        title: 'Multi-Platform Optimization',
        description:
          'All content is optimized for flawless performance on mobile and desktop devices.',
      },
    ],
    overlayPositions: () => [
      { top: '58%', left: '0' },
      { top: '20%', left: '17%' },
      { top: '58%', left: '36%' },
      { top: '20%', left: '55%' },
      { top: '58%', left: '71%' },
    ],
  },
);

// Single computed src instead of v-if/v-else
const bgImageSrc = computed(() => isLight.value ? props.bgImageLight : props.bgImageDark);

const overlayTitleStyle = computed(() => ({
  width: '249px',
  color: isLight.value ? '#613FAA' : '#A561FF',
  fontSize: 'clamp(16px, 2.5vw, 20px)',
  fontWeight: 600,
  lineHeight: 'normal',
}));

const overlayDescStyle = computed(() => ({
  color: isLight.value ? '#000000' : '#D9D9D9',
  fontSize: 'clamp(13px, 2vw, 15px)',
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '0.3px',
}));

const mobileCardStyle = computed(() => ({
  background:
    isLight.value
      ? '#FFFFFF'
      : 'linear-gradient(93deg, #15052E 33.61%, #2A0B55 96.74%)',
  border:
    isLight.value
      ? '1px solid rgba(34, 13, 66, 0.08)'
      : '1px solid #3D1B60',
}));

const mobileTitleStyle = computed(() => ({
  color: isLight.value ? '#613FAA' : '#A561FF',
  fontSize: 'clamp(16px, 3vw, 20px)',
  fontWeight: 600,
}));

const mobileDescStyle = computed(() => ({
  color: isLight.value ? '#000000' : '#D9D9D9',
  fontSize: '15px',
  fontWeight: 400,
}));
</script>

<template>
  <section class="relative z-10 mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Why we are different">
    <!-- Heading -->
    <h2
      class="text-center text-[28px] lg:text-[48px] font-semibold leading-tight px-4 font-avant-demi text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
    >
      {{ heading }}
    </h2>

    <!-- Subcopy -->
    <p
      class="text-center max-w-[720px] mx-auto mt-4 text-[16px] lg:text-[18px] font-normal tracking-[0.36px] lg:opacity-70 font-avant"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subcopy }}
    </p>

    <!-- ==================== DESKTOP SVG ==================== -->
    <div class="relative w-full max-w-[973px] mx-auto mt-10 hidden lg:block">
      <!-- Dark/Light SVG graphic -->
      <NuxtImg
        :src="bgImageSrc"
        :alt="heading"
        loading="lazy"
        class="w-full h-auto block"
      />

      <!-- Desktop overlay cards (positioned to match the SVG) -->
      <div
        v-for="(item, index) in items"
        :key="`desktop-${index}`"
        class="absolute border-none bg-none p-0"
        :style="{
          top: overlayPositions[index]?.top || '50%',
          left: overlayPositions[index]?.left || '0',
        }"
      >
        <h3
          class="m-0 font-avant"
          :class="index === 0 ? '' : 'text-left'"
          :style="index === 4 ? { ...overlayTitleStyle, width: '320px' } : overlayTitleStyle"
        >
          {{ item.title }}
        </h3>
        <p
          class="text-left mt-[8px] font-avant"
          :style="{ ...overlayDescStyle, maxWidth: '288px' }"
        >
          {{ item.description }}
        </p>
      </div>
    </div>

    <!-- ==================== MOBILE CARDS ==================== -->
    <div
      class="flex flex-col items-center gap-4 mt-6 lg:hidden w-full max-w-[500px] mx-auto px-4"
    >
      <article
        v-for="item in items"
        :key="item.title"
        class="w-full rounded-[9px] p-5 text-left"
        :style="mobileCardStyle"
      >
        <h3 class="m-0 font-avant" :style="mobileTitleStyle">{{ item.title }}</h3>
        <p class="mt-2 font-avant" :style="mobileDescStyle">{{ item.description }}</p>
      </article>
    </div>
  </section>
</template>
